'use client';

import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Li, Ul } from '@lichtblick/photon/atoms/list/list';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { TextCTA } from '@lichtblick/photon/atoms/text-cta/text-cta';
import { Footnote } from '@lichtblick/photon/molecules/footnote/footnote';
import { Modal } from '@lichtblick/photon/organisms/modal/modal';
import { useState } from 'react';

import { ModalContent } from '../components/modals/modal-content';
import { i18n } from '../i18n';

const GAS_MODAL_TITLE = 'Was ist Biogas?';

export const GasModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <TextCTA
        as="button"
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        {GAS_MODAL_TITLE}
      </TextCTA>

      <Modal
        footerContent={
          <TextCTA
            as="button"
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            {i18n.modal.confirm}
          </TextCTA>
        }
        isOpen={isModalOpen}
        onPointerDownOutside={() => {
          setIsModalOpen(false);
        }}
        title={GAS_MODAL_TITLE}
      >
        <ModalContent>
          <Headline renderAs="h1" size="s">
            {GAS_MODAL_TITLE}
          </Headline>
          <Text renderAs="p">
            Biogas kann fossiles Erdgas 1:1 ersetzen und gilt als erneuerbare Energie. Das Biogas von LichtBlick besteht
            ausschließlich aus Rest- und Abfallstoffen wie z. B. Biomüll aus Haushalten oder Schnittgut.
          </Text>
          <Headline renderAs="h2" size="s">
            Wieviel CO₂ kann ich mit Biogas einsparen?
          </Headline>
          <Text renderAs="p">
            Der Biogasanteil¹ der LichtBlick Gas-Tarife bestimmt, wie viel CO₂-Emissionen im Durchschnitt gegenüber dem
            Erdgasanteil eingespart werden können²:
          </Text>

          <Ul>
            <Li>
              <strong>LichtBlick Gas</strong> mit 10&nbsp;% Biogasanteil spart ca. <strong>14,55&nbsp;% CO₂</strong>{' '}
              ein, das entspricht einer CO₂-Reduktion von 30,8&nbsp;g/kWh.
            </Li>
            <Li>
              <strong>LichtBlick Gas Plus</strong> mit 30&nbsp;% Biogasanteil spart ca.{' '}
              <strong>26,40&nbsp;% CO₂</strong> ein, das entspricht einer CO₂-Reduktion von 55,9&nbsp;g/kWh.
            </Li>
            <Li>
              <strong>LichtBlick BioGas</strong> mit 100&nbsp;% Biogas spart ca. <strong>67,87&nbsp;% CO₂</strong> ein,
              das entspricht einer CO₂-Reduktion von 143,6&nbsp;g/kWh.
            </Li>
          </Ul>

          <Headline renderAs="h2" size="s">
            Gut zu wissen:
          </Headline>

          <Ul>
            <Li>
              Mit den Tarifen <strong>LichtBlick Gas</strong> und <strong>LichtBlick Gas</strong> Plus fördert
              LichtBlick Klimaschutzprojekte außerhalb der eigenen Wertschöpfungskette. Dafür werden Zertifikate aus
              nach dem Gold Standard zertifizierten Projekten erworben und stillgelegt. Die Projekte sollen dazu
              beitragen, Treibhausgasemissionen einzusparen oder zu binden. Dieser Beitrag verändert nicht die
              physikalische Gaslieferung und wird nicht als Ausgleich der Emissionen des Gasverbrauchs verstanden.
            </Li>
            <Li>
              <strong>Gas mit Biogasanteil verdrängt Erdgas aus dem Netz</strong>. Das heißt, ihr könnt mit eurem
              Gas-Tarif aktiv dazu beitragen, dass mehr emissionsreduziertes³ Gas und weniger fossiles Erdgas ins Netz
              eingespeist werden.
            </Li>
          </Ul>

          <div>
            <Footnote index="1">
              Die Beimischung von Biogas erfolgt bilanziell: In Höhe des in eurem Tarif ausgewiesenen Biogas-Anteils
              (10&nbsp;%, 30&nbsp;% oder 100&nbsp;% eures Jahresverbrauchs) wird Biogas aus erneuerbaren Quellen (z. B.
              Reststoffen) erzeugt und in das deutsche bzw. europäische Erdgasnetz eingespeist. Der Nachweis erfolgt
              über zertifizierte Biogas-Herkunftsnachweise, die in einem anerkannten Register (Biogasregister der dena
              bzw. Herkunftsnachweisregister für Gas beim Umweltbundesamt) entwertet werden. Die tatsächliche
              Gaslieferung erfolgt physikalisch über das allgemeine Gasnetz, in dem sich Gase unterschiedlicher Herkunft
              mischen. Es kann daher nicht sichergestellt werden, dass das konkret bei euch ankommende Gas physisch aus
              Biogas besteht. Durch Einkauf und Entwertung der Herkunftsnachweise wird jedoch sichergestellt, dass eine
              einmal aus erneuerbaren Quellen erzeugte und ins Netz eingespeiste Gasmenge auch nur einmal unter dieser
              Eigenschaft entnommen und beworben werden kann.
            </Footnote>
            <Footnote index="2">
              LichtBlick verwendet in seiner Kalkulation den Emissionsfaktor des TÜV Nord in Höhe von
              0,21162kg&nbsp;CO₂e je kWh (Brennwert Hs), um die Erdgasmengen in Treibhausgasemissionen (in CO₂e)
              umzurechnen.
            </Footnote>
            <Footnote index="3">
              "Emissionsreduziert" bedeutet, dass durch die Einspeisung von Biogas (je nach vereinbartem Tarif
              10&nbsp;%, 30&nbsp;% oder 100&nbsp;% eures Verbrauchs) in das Gasnetz die mit eurem Gasverbrauch
              verbundenen Emissionen bilanziell reduziert werden. Die konkreten Emissionsfaktoren (in g CO₂e/kWh) sowie
              die daraus resultierende prozentuale Emissionsreduktion gegenüber reinem Erdgas hängen von eurer Tarifwahl
              ab. Ergänzend gilt der Hinweis zur bilanziellen Beimischung (siehe ¹).
            </Footnote>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
